import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShoppingCart, CheckCircle2, User } from 'lucide-react';
import { api } from '../../lib/api';
import { Pagination } from '../../components/admin/Pagination';
import { SegmentedTabs } from '../../components/admin/SegmentedTabs';
import { SortableTh, TableSkeleton } from '../../components/admin/SortableTable';
import { TableEmpty } from '../../components/admin/TableEmpty';
import { useCurrency } from '../../context/CurrencyContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const TABS = [
    { key: 'active', label: 'Active' },
    { key: 'converted', label: 'Converted' },
    { key: 'all', label: 'All' },
];

export default function AdminCarts() {
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState('active');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [counts, setCounts] = useState({ active: 0, converted: 0 });
    const [selected, setSelected] = useState(null);
    const [sort, setSort] = useState({ by: 'updated_at', dir: 'desc' });
    const [loading, setLoading] = useState(true);
    const PAGE_SIZE = 10;
    const { format } = useCurrency();

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/admin/carts', { params: { status, page, page_size: PAGE_SIZE, sort_by: sort.by, sort_dir: sort.dir } });
            setItems(r.data.items);
            setTotal(r.data.total);
            setCounts({ active: r.data.active_count, converted: r.data.converted_count });
        } catch { toast.error('Failed to load carts.'); }
        finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [status, page, sort]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setPage(1); }, [status, sort]);

    return (
        <div data-testid="admin-carts-page">
            <div className="mb-8">
                <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>Atelier</p>
                <h1 className="font-serif text-3xl sm:text-4xl mt-1" style={{ color: 'var(--brand-burgundy)' }}>Carts</h1>
                <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>Track shopping bags — active (in progress) vs converted (checked out).</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
                <div className="luxury-card p-4 flex items-center gap-3" data-testid="admin-carts-active-stat">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--brand-rose)' }}><ShoppingCart className="w-5 h-5" style={{ color: 'var(--brand-burgundy)' }} /></div>
                    <div><p className="font-serif text-3xl" style={{ color: 'var(--brand-burgundy)' }}>{counts.active}</p><p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Active</p></div>
                </div>
                <div className="luxury-card p-4 flex items-center gap-3" data-testid="admin-carts-converted-stat">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--brand-rose)' }}><CheckCircle2 className="w-5 h-5" style={{ color: 'var(--brand-burgundy)' }} /></div>
                    <div><p className="font-serif text-3xl" style={{ color: 'var(--brand-burgundy)' }}>{counts.converted}</p><p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Converted</p></div>
                </div>
            </div>

            <div className="flex items-center justify-between gap-4 mb-6">
                <SegmentedTabs value={status} onChange={setStatus} options={TABS} testIdPrefix="admin-carts-tab" />
                <p className="text-xs whitespace-nowrap hidden sm:block" style={{ color: 'var(--ink-muted)' }} data-testid="admin-carts-count">{total} cart{total === 1 ? '' : 's'}</p>
            </div>

            <div className="luxury-card overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]" data-testid="admin-carts-table">
                    <thead className="bg-[var(--brand-rose)]/40">
                        <tr className="text-left">
                            <th className="p-4">Shopper</th>
                            <SortableTh label="Items" field="item_count" sort={sort} onSort={setSort} />
                            <SortableTh label="Subtotal" field="subtotal" sort={sort} onSort={setSort} />
                            <SortableTh label="Status" field="status" sort={sort} onSort={setSort} />
                            <SortableTh label="Updated" field="updated_at" sort={sort} onSort={setSort} />
                        </tr>
                    </thead>
                    {loading ? <TableSkeleton cols={5} /> : (
                    <tbody>
                        {items.map((c) => (
                            <tr key={c.id} onClick={() => setSelected(c)} className="border-t border-[rgba(90,36,50,0.08)] cursor-pointer hover:bg-[var(--brand-rose)]/20 transition-colors" data-testid={`admin-cart-row-${c.id}`}>
                                <td className="p-4">
                                    <div className="flex items-center gap-2">
                                        <User className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                                        <div className="min-w-0">
                                            <p className="truncate" style={{ color: 'var(--brand-burgundy)' }}>{c.customer_name || c.customer_email || 'Guest'}</p>
                                            <p className="text-[11px] font-mono truncate" style={{ color: 'var(--ink-muted)' }}>{c.customer_email || c.visitor_id}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-4">{c.items?.length || 0}</td>
                                <td className="p-4">{format(c.subtotal || 0)}</td>
                                <td className="p-4"><span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: c.status === 'converted' ? '#1F7A5A' : '#B7791F' }}>{c.status}</span></td>
                                <td className="p-4 text-xs" style={{ color: 'var(--ink-muted)' }}>{c.updated_at ? new Date(c.updated_at).toLocaleString() : '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                    )}
                </table>
                </div>
                {!loading && items.length === 0 && (
                    <TableEmpty icon={ShoppingCart} title="No carts" subtitle="Shopping bags will appear here as visitors add items." testId="admin-carts-empty" />
                )}
            </div>

            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testIdPrefix="admin-carts-pagination" />

            <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white" data-testid="admin-cart-detail-dialog">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>Cart Details</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>{selected.customer_name || selected.customer_email || 'Guest'} · <span className="capitalize">{selected.status}</span>{selected.reference ? <> · <span className="font-mono text-xs">{selected.reference}</span></> : null}</p>
                                {(selected.customer_email || selected.customer_phone || selected.address) && (
                                    <div className="rounded-lg border border-[rgba(90,36,50,0.10)] p-3 text-sm space-y-2" data-testid="admin-cart-details">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                                            {selected.customer_name && <div><p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Name</p><p>{selected.customer_name}</p></div>}
                                            {selected.customer_email && <div><p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Email</p><a href={`mailto:${selected.customer_email}`} className="hover:underline" style={{ color: 'var(--brand-burgundy)' }}>{selected.customer_email}</a></div>}
                                            {selected.customer_phone && <div><p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Phone</p><p>{selected.customer_phone}</p></div>}
                                            {(selected.address || selected.city || selected.country) && <div><p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Shipping</p><p>{[selected.address, [selected.city, selected.country].filter(Boolean).join(', '), selected.postal_code].filter(Boolean).join(' · ')}</p></div>}
                                        </div>
                                        {selected.notes && <div><p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Notes</p><p className="text-sm italic">"{selected.notes}"</p></div>}
                                    </div>
                                )}
                                <ul className="divide-y divide-[rgba(90,36,50,0.08)] border border-[rgba(90,36,50,0.08)] rounded-lg">
                                    {(selected.items || []).map((it, i) => (
                                        <li key={`${it.product_id}-${it.size}-${it.color}-${i}`} className="p-3 flex items-center gap-3">
                                            {it.image && <img src={it.image} alt={it.name} className="w-12 h-14 object-cover rounded" />}
                                            <div className="flex-1"><p className="font-serif text-base" style={{ color: 'var(--brand-burgundy)' }}>{it.name}</p><p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{[it.size, it.color, `Qty ${it.quantity}`].filter(Boolean).join(' · ')}</p>{it.special_request && <p className="text-xs mt-0.5 italic" style={{ color: 'var(--brand-burgundy)' }}>Note: {it.special_request}</p>}</div>
                                            <p className="text-sm">{format(it.price * it.quantity)}</p>
                                        </li>
                                    ))}
                                </ul>
                                <div className="text-right font-serif text-xl" style={{ color: 'var(--brand-burgundy)' }}>Total: {format(selected.subtotal || 0)}</div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
